import { tenantBasePath } from '../../lib/tenant.ts';

export interface OperationalAlert {
  readonly id: string;
  readonly severity: 'critical' | 'warning' | 'info';
  readonly title: string;
  readonly description: string;
  readonly entityType: string;
  readonly recordId: string;
  readonly metricLabel: string;
  readonly metricValue: string;
  readonly actionLabel: string;
  readonly proposedAction: string;
  readonly targetRoute: string;
  readonly requiredCapability: string;
}

export function formatRial(value: string | number): string {
  const n = BigInt(value || '0');
  return `${n.toLocaleString('fa-IR')} ریال`;
}

export function getToolEndpoint(moduleId: string, slug: string): string | null {
  const base = `${tenantBasePath(slug)}/api/v1/ai/tools`;
  switch (moduleId) {
    case 'finance':
      return `${base}/finance/open-items`;
    case 'crm':
      return `${base}/sales/open-opportunities`;
    case 'procurement':
      return `${base}/procurement/open-orders`;
    case 'inventory':
      return `${base}/inventory/stock-summary`;
    case 'projects':
      return `${base}/projects/overdue-tasks`;
    default:
      return null;
  }
}

export function parseOperationalAlerts(
  moduleId: string,
  data: unknown,
): readonly OperationalAlert[] {
  if (!data || typeof data !== 'object') return [];
  const alerts: OperationalAlert[] = [];

  switch (moduleId) {
    case 'finance': {
      const fin = data as {
        receivable?: {
          lines?: readonly {
            recordId: string;
            openBalanceRial: string;
            dueDate: string;
            status: string;
          }[];
        };
        payable?: {
          lines?: readonly {
            recordId: string;
            openBalanceRial: string;
            dueDate: string;
            status: string;
          }[];
        };
      };
      for (const line of fin.receivable?.lines ?? []) {
        if (BigInt(line.openBalanceRial || '0') > 0n) {
          alerts.push({
            id: `ar-${line.recordId}`,
            severity: 'critical',
            title: `مطالبه باز: سند ${line.recordId}`,
            description: `سررسید: ${line.dueDate} — وضعیت: ${line.status === 'open' ? 'تسویه‌نشده' : line.status}`,
            entityType: 'مطالبه',
            recordId: line.recordId,
            metricLabel: 'مانده بدهکار',
            metricValue: formatRial(line.openBalanceRial),
            actionLabel: 'پیش‌نمایش پیگیری وصول',
            proposedAction: 'بررسی حساب طرف در دفتر معین و ثبت یادآوری وصول',
            targetRoute: '/finance/open-items',
            requiredCapability: 'accounting.ar-open-item.read',
          });
        }
      }
      for (const line of fin.payable?.lines ?? []) {
        if (BigInt(line.openBalanceRial || '0') > 0n) {
          alerts.push({
            id: `ap-${line.recordId}`,
            severity: 'warning',
            title: `بدهی سررسیدشده: سند ${line.recordId}`,
            description: `سررسید: ${line.dueDate} — وضعیت: ${line.status === 'open' ? 'پرداخت‌نشده' : line.status}`,
            entityType: 'بدهی',
            recordId: line.recordId,
            metricLabel: 'مانده بستانکار',
            metricValue: formatRial(line.openBalanceRial),
            actionLabel: 'پیش‌نمایش تسویه بدهی',
            proposedAction: 'بررسی فاکتور خرید و ارجاع به دستور پرداخت خزانه‌داری',
            targetRoute: '/finance/open-items',
            requiredCapability: 'accounting.ap-open-item.read',
          });
        }
      }
      break;
    }
    case 'crm': {
      const crm = data as {
        lines?: readonly {
          recordId: string;
          title: string;
          stage: string | null;
          amountRial: string;
        }[];
      };
      for (const line of crm.lines ?? []) {
        alerts.push({
          id: `crm-${line.recordId}`,
          severity: 'warning',
          title: `فرصت فروش: ${line.title || line.recordId}`,
          description: `مرحله فروش: ${line.stage || 'در جریان'}`,
          entityType: 'سرنخ فروش',
          recordId: line.recordId,
          metricLabel: 'ارزش برآوردی',
          metricValue: formatRial(line.amountRial),
          actionLabel: 'پیش‌نمایش پیگیری فروش',
          proposedAction: 'ثبت تماس پیگیری با مشتری و ثبت نتیجه مذاکره',
          targetRoute: '/crm/leads',
          requiredCapability: 'crm.lead.manage',
        });
      }
      break;
    }
    case 'procurement': {
      const proc = data as {
        lines?: readonly { recordId: string; status: string; amountRial: string }[];
      };
      for (const line of proc.lines ?? []) {
        alerts.push({
          id: `po-${line.recordId}`,
          severity: 'warning',
          title: `سفارش خرید: ${line.recordId}`,
          description: `وضعیت تحویل: ${line.status === 'approved' ? 'تأییدشده در انتظار تحویل' : 'تحویل جزئی'}`,
          entityType: 'سفارش خرید',
          recordId: line.recordId,
          metricLabel: 'مبلغ سفارش',
          metricValue: formatRial(line.amountRial),
          actionLabel: 'پیش‌نمایش رسید انبار',
          proposedAction: 'هماهنگی با انبار و ثبت رسید ورود کالا',
          targetRoute: '/procurement/orders',
          requiredCapability: 'procurement.order.read',
        });
      }
      break;
    }
    case 'inventory': {
      const items = Array.isArray(data) ? data : [];
      for (const line of items as readonly {
        recordId: string;
        label: string;
        onHandNumerator: number | string;
      }[]) {
        alerts.push({
          id: `inv-${line.recordId}`,
          severity: 'critical',
          title: `کسری موجودی: ${line.label}`,
          description: 'موجودی انبار در سطح حداقل یا نقطه سفارش',
          entityType: 'کالای انبار',
          recordId: line.recordId,
          metricLabel: 'موجودی فعلی',
          metricValue: `${line.onHandNumerator} واحد`,
          actionLabel: 'پیش‌نمایش درخواست خرید',
          proposedAction: 'ایجاد درخواست تأمین مجدد کالا از واحد خرید',
          targetRoute: '/inventory/stock',
          requiredCapability: 'inventory.stock.read',
        });
      }
      break;
    }
    case 'projects': {
      const prj = data as {
        lines?: readonly {
          recordId: string;
          title: string;
          projectRef: string | null;
          dueDate: string;
          daysOverdue: number;
        }[];
      };
      for (const line of prj.lines ?? []) {
        alerts.push({
          id: `prj-${line.recordId}`,
          severity: 'critical',
          title: `کار عقب‌افتاده: ${line.title}`,
          description: `پروژه: ${line.projectRef || 'عمومی'} — سررسید: ${line.dueDate}`,
          entityType: 'وظیفه',
          recordId: line.recordId,
          metricLabel: 'میزان تأخیر',
          metricValue: `${line.daysOverdue} روز تأخیر`,
          actionLabel: 'پیش‌نمایش تعیین تکلیف',
          proposedAction: 'تمدید مهلت یا تخصیص مجدد به عضو دیگر تیم پروژه',
          targetRoute: '/projects/tasks',
          requiredCapability: 'projects.task.manage',
        });
      }
      break;
    }
  }

  return alerts;
}
